import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { kaseyaAPI } from '../../services/kaseya-api'

export interface OrganizationLive {
  id: string
  name: string
  slug: string
  logo_url?: string | null
  subscription_tier: 'essentials' | 'enhanced' | 'premium'
  kaseya_organization_id: number | null
  kaseya_group_id: number | null
  kaseya_live_name?: string
  kaseya_synced: boolean
  last_synced_at: string | null
  user_count: number
  device_count: number
  ticket_count: number
  contact_phone?: string | null
  contact_address?: string | null
  created_at: string
  updated_at: string
}

/**
 * Hook to fetch organizations with live Kaseya data
 * Uses hybrid approach: local DB + cached Kaseya data
 * Refreshes cache every 60 seconds to pick up auto-sync updates
 */
export const useOrganizationsLive = () => {
  return useQuery({
    queryKey: ['organizationsLive'],
    queryFn: async () => {
      // 1. Fetch local companies with counts (fast, from DB)
      const { data: localCompanies, error: localError } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          slug,
          logo_url,
          subscription_tier,
          contact_phone,
          contact_address,
          kaseya_organization_id,
          kaseya_group_id,
          last_synced_at,
          created_at,
          updated_at,
          users:users(count),
          devices:devices(count),
          tickets:tickets(count)
        `)
        .order('created_at', { ascending: false })

      if (localError) throw localError

      // 2. Fetch cached Kaseya organizations (from auto-sync)
      const { data: kaseyaCache, error: kaseyaError } = await supabase
        .from('kaseya_organizations_cache')
        .select('kaseya_organization_id, name, last_synced_at')

      if (kaseyaError) {
        console.warn('Failed to fetch Kaseya cache:', kaseyaError)
      }

      // 2b. Fetch device counts from cache (paginated to bypass 1000 limit)
      const deviceCountByGroup = new Map<number, number>()

      try {
        let offset = 0
        const pageSize = 1000
        let hasMore = true

        while (hasMore) {
          const { data: deviceCounts } = await supabase
            .from('kaseya_devices_cache')
            .select('group_id')
            .range(offset, offset + pageSize - 1)

          if (deviceCounts && deviceCounts.length > 0) {
            for (const device of deviceCounts) {
              const count = deviceCountByGroup.get(device.group_id) || 0
              deviceCountByGroup.set(device.group_id, count + 1)
            }
            offset += pageSize
            hasMore = deviceCounts.length === pageSize
          } else {
            hasMore = false
          }
        }
      } catch (err) {
        console.warn('Failed to fetch device counts:', err)
      }

      // 3. Build lookup map for fast merging
      const kaseyaMap = new Map(
        kaseyaCache?.map(org => [org.kaseya_organization_id, org]) || []
      )

      // 4. Merge local + cached Kaseya data
      const merged: OrganizationLive[] = (localCompanies || []).map(company => {
        const kaseyaOrg = company.kaseya_organization_id
          ? kaseyaMap.get(company.kaseya_organization_id)
          : null

        // Get device count from cache if company has kaseya_group_id, else from devices table
        const cacheDeviceCount = company.kaseya_group_id
          ? deviceCountByGroup.get(company.kaseya_group_id) || 0
          : 0
        const localDeviceCount = company.devices?.[0]?.count || 0

        return {
          id: company.id,
          name: company.name,
          slug: company.slug,
          logo_url: company.logo_url,
          subscription_tier: company.subscription_tier,
          contact_phone: company.contact_phone,
          contact_address: company.contact_address,
          kaseya_organization_id: company.kaseya_organization_id,
          kaseya_group_id: company.kaseya_group_id,
          kaseya_live_name: kaseyaOrg?.name,
          kaseya_synced: !!kaseyaOrg,
          // Use cache timestamp for synced companies (more accurate)
          last_synced_at: kaseyaOrg?.last_synced_at || company.last_synced_at,
          user_count: company.users?.[0]?.count || 0,
          // Use cache count for synced companies, local count for non-synced
          device_count: company.kaseya_group_id ? cacheDeviceCount : localDeviceCount,
          ticket_count: company.tickets?.[0]?.count || 0,
          created_at: company.created_at,
          updated_at: company.updated_at,
        }
      })

      return merged
    },
    // Refresh every 60 seconds to pick up auto-sync cache updates
    refetchInterval: 60000,
    staleTime: 30000, // Consider data stale after 30 seconds
  })
}

/**
 * Manual refresh hook - fetches LIVE from Kaseya API immediately
 * Use this when user clicks "Refresh from Kaseya" button
 */
export const useRefreshOrganizationsFromKaseya = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      console.log('🔄 Manual refresh from Kaseya started...')

      // Fetch live organizations from Kaseya
      const liveOrgs = await kaseyaAPI.getOrganizations()
      console.log(`Fetched ${liveOrgs.length} organizations from Kaseya`)

      // Update cache immediately
      const upsertPromises = liveOrgs.map(org =>
        supabase.from('kaseya_organizations_cache').upsert({
          kaseya_organization_id: org.Id,
          name: org.Name,
          has_custom_fields: org.HasCustomFields,
          last_synced_at: new Date().toISOString(),
          raw_data: org,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'kaseya_organization_id'
        })
      )

      await Promise.all(upsertPromises)
      console.log('✅ Cache updated with live data')

      // Also fetch groups for complete sync
      const liveGroups = await kaseyaAPI.getGroups()
      console.log(`Fetched ${liveGroups.length} groups from Kaseya`)

      const groupUpsertPromises = liveGroups.map(group =>
        supabase.from('kaseya_groups_cache').upsert({
          kaseya_group_id: group.Id,
          name: group.Name,
          parent_site_id: group.ParentSiteId,
          parent_site_name: group.ParentSiteName,
          parent_organization_id: group.ParentOrganizationId,
          parent_organization_name: group.ParentOrganizationName,
          notes: group.Notes,
          last_synced_at: new Date().toISOString(),
          raw_data: group,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'kaseya_group_id'
        })
      )

      await Promise.all(groupUpsertPromises)
      console.log('✅ Groups cache updated')

      return { organizations: liveOrgs.length, groups: liveGroups.length }
    },
    onSuccess: (result) => {
      // Invalidate queries to trigger re-fetch with new cache data
      queryClient.invalidateQueries({ queryKey: ['organizationsLive'] })
      console.log(`✅ Manual refresh complete: ${result.organizations} orgs, ${result.groups} groups`)
    },
    onError: (error) => {
      console.error('❌ Manual refresh failed:', error)
    }
  })
}

/**
 * Get sync status summary
 * Shows how many orgs are synced vs not synced
 */
export const useOrganizationsSyncStatus = () => {
  const { data: organizations = [] } = useOrganizationsLive()

  const syncedCount = organizations.filter(org => org.kaseya_synced).length
  const notSyncedCount = organizations.filter(org => !org.kaseya_synced).length
  const lastSyncTime = organizations
    .filter(org => org.last_synced_at)
    .map(org => new Date(org.last_synced_at!))
    .sort((a, b) => b.getTime() - a.getTime())[0]

  return {
    total: organizations.length,
    synced: syncedCount,
    notSynced: notSyncedCount,
    lastSync: lastSyncTime,
  }
}
